import React, { useState, useEffect } from "react";
import { FaUser, FaLock, FaEye, FaEyeSlash, FaWhatsapp } from "react-icons/fa";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Login.css";

const API_BASE = "https://dgps.co.in/api";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(0); // countdown timer for resend
  const [expiredSchoolId, setExpiredSchoolId] = useState(null);

  const togglePasswordVisibility = () => setShowPassword((s) => !s);

  // countdown logic
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setExpiredSchoolId(null);

    if (!/^\d{10}$/.test(mobile)) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }
    if (step === "login" && password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (step === "otp" && otp.length !== 6) {
      setError("Enter the 6-digit OTP sent to your WhatsApp.");
      return;
    }

    setLoading(true);
    try {
      const payload =
        step === "login"
          ? { mobile, password }
          : { mobile, password, otp };

      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      console.log("Login Response:", data);

      // OTP Requested
      if (data?.status && data?.message?.includes("OTP sent")) {
        toast.success("OTP sent successfully!");
        // Swal.fire({
        //   icon: "info",
        //   title: "OTP Sent ✅",
        //   text: "Check your WhatsApp for the OTP.",
        //   timer: 1500,
        //   showConfirmButton: false,
        // });
        setStep("otp");
        setTimer(60); // start 1-min timer
        setLoading(false);
        return;
      }

      // Subscription expired
      if (data?.status === false && data?.message?.includes("Subscription expired")) {
        const sid = data.school_id || null;
        if (sid) setExpiredSchoolId(sid);
        toast.error(data.message || "Subscription expired.");
        setLoading(false);
        return;
      }

      // Login Successful
      if (data?.status === true && data?.token) {
        let chosenSchoolId = data.default_school_id || (data.schools?.[0]?.id ?? "");
        localStorage.setItem("token", data.token);
        localStorage.setItem("id", data.user.id);
        localStorage.setItem("name", data.user.name);
        localStorage.setItem("schools", JSON.stringify(data.schools || []));
        localStorage.setItem("school_id", chosenSchoolId);
        const chosen =
          (data.schools || []).find((s) => String(s.id) === String(chosenSchoolId)) ||
          data.schools?.[0];
        localStorage.setItem("role", chosen?.role || "");
        localStorage.setItem("userType", data.user.userType);

        await Swal.fire({
          icon: "success",
          title: "Login Successful!",
          text: `Welcome, ${data.user.name}`,
          timer: 1400,
          showConfirmButton: false,
        });

        window.location.href = "/admin/dashboard";
        return;
      }

      throw new Error(data.message || "Invalid credentials.");
    } catch (err) {
      console.error("Login Error:", err);
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (timer > 0) return;
    setTimer(60);
    handleSubmit(); // resend OTP call
  };

  // 🔁 Renew button
  const handleRenewType = async (schoolId, type) => {
    Swal.fire({
      title: "Please wait...",
      text: "Redirecting to payment gateway...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const res = await fetch(`${API_BASE}/schoolsubscription/pay/${schoolId}?type=${type}`);
      const data = await res.json();
      Swal.close();

      if (data?.status && data?.payment_url) {
        Swal.fire({
          title: "Redirecting...",
          text: `Payment Amount: ₹${data.amount}/- (${data.type.toUpperCase()})`,
          icon: "info",
          showConfirmButton: false,
          timer: 1500,
        });
        setTimeout(() => {
          window.location.href = data.payment_url;
        }, 1500);
      } else {
        Swal.fire({
          icon: "error",
          title: "Payment Failed",
          text: data.message || "Unable to initiate payment.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Something went wrong.",
      });
    }
  };


  const showRenewOptions = async (schoolId) => {
    try {
      const res = await fetch(`${API_BASE}/schoolsubscription/pay/${schoolId}?info=1`);
      const data = await res.json();

      if (!data?.status) {
        Swal.fire({
          icon: "error",
          title: "Plan Not Found",
          text: data.message || "Unable to fetch plan details",
        });
        return;
      }

      const { monthly_fee, yearly_fee, plan_name, school_name } = data.data;

      Swal.fire({
        title: `${school_name}`,
        html: `
        <div style="font-size:16px; text-align:center;">
          <p>Select your renewal type:</p>
          <button id="monthlyBtn" class="swal2-confirm swal2-styled" style="background:#28a745;">
            Monthly @ ₹${monthly_fee}/-
          </button>
          <button id="yearlyBtn" class="swal2-confirm swal2-styled" style="background:#007bff; margin-top:10px;">
            Yearly @ ₹${yearly_fee}/-
          </button>
        </div>
      `,
        showConfirmButton: false,
        didOpen: () => {
          const monthlyBtn = document.getElementById("monthlyBtn");
          const yearlyBtn = document.getElementById("yearlyBtn");

          monthlyBtn.addEventListener("click", () => handleRenewType(schoolId, "monthly"));
          yearlyBtn.addEventListener("click", () => handleRenewType(schoolId, "yearly"));
        },
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Something went wrong while fetching plans",
      });
    }
  };

  return (
    <div className="login-container">
      <ToastContainer position="top-right" autoClose={2000} theme="colored" />
      <form className="login-box" onSubmit={handleSubmit}>
        <h2>🎓 School Portal Login</h2>
        <p className="subtitle">
          {step === "login" ? "Access for Super Admin, Admin & Staff" : "Enter OTP to Verify"}
        </p>

        {error && (
          <div className="error-msg" style={{ color: "red", textAlign: "center" }}>
            {error}
          </div>
        )}

        {expiredSchoolId && (
          <div style={{ textAlign: "center", marginTop: "10px" }}>
            <button
              type="button"
              onClick={() => showRenewOptions(expiredSchoolId)}
              className="renew-btn"
              style={{
                background: "#008CBA",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                cursor: "pointer",
                width: "100%",
                marginBottom: "10px",
              }}
            >
              🔁 Renew Now
            </button>
          </div>
        )}


        {/* Mobile */}
        <div className="input-group">
          <FaUser className="icon" />
          <input
            type="text"
            placeholder="Mobile Number"
            value={mobile}
            maxLength={10}
            inputMode="numeric"
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
            disabled={step === "otp"}
          />
        </div>

        {/* Password */}
        {step === "login" && (
          <div className="input-group">
            <FaLock className="icon" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {showPassword ? (
              <FaEyeSlash className="toggle-eye" onClick={togglePasswordVisibility} />
            ) : (
              <FaEye className="toggle-eye" onClick={togglePasswordVisibility} />
            )}
          </div>
        )}

        {/* OTP Box */}
        {step === "otp" && (
          <div className="otp-box">
            <FaWhatsapp className="icon" color="#25D366" />
            <input
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otp}
              maxLength={6}
              inputMode="numeric"
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            />
            <button
              type="button"
              className={`resend-btn ${timer > 0 ? "disabled" : ""}`}
              onClick={handleResendOtp}
              disabled={timer > 0}
            >
              {timer > 0 ? `Resend in 0:${timer.toString().padStart(2, "0")}` : "Resend OTP"}
            </button>
          </div>
        )}

        <button className="login-btn" type="submit" disabled={loading}>
          {loading
            ? step === "otp"
              ? "Verifying..."
              : "Processing..."
            : step === "otp"
              ? "Verify OTP"
              : "Login"}
        </button>
      </form>
    </div>
  );
}
